import dayjs from "dayjs";
import { startOfWeekMonday } from "../../components/common/DateRangePicker";

// Central date-period/comparison engine for the Dashboard - every
// comparison-capable widget resolves its window and its comparison window
// through this one file, so no widget ever does its own date arithmetic.
// Built on `dayjs` (already a project dependency, already used the same
// way by components/common/DateRangePicker.jsx's own PRESETS) rather than
// a new date library.
const fmt = (d) => d.format("YYYY-MM-DD");

// ---- Section 4: global Period options ----
export const PERIOD_OPTIONS = [
  { value: "TODAY", label: "Today" },
  { value: "YESTERDAY", label: "Yesterday" },
  { value: "THIS_WEEK", label: "This Week" },
  { value: "THIS_MONTH", label: "This Month" },
  { value: "LAST_30_DAYS", label: "Last 30 Days" },
  { value: "THIS_YEAR", label: "This Year" },
  { value: "CUSTOM", label: "Custom" },
];

export const resolvePeriod = (periodKey, customFrom, customTo) => {
  const today = dayjs();
  switch (periodKey) {
    case "TODAY":
      return { dateFrom: fmt(today), dateTo: fmt(today), label: "Today" };
    case "YESTERDAY": {
      const y = today.subtract(1, "day");
      return { dateFrom: fmt(y), dateTo: fmt(y), label: "Yesterday" };
    }
    case "THIS_WEEK":
      // Monday-based, same week as the phone app and the list pages' picker.
      return { dateFrom: fmt(startOfWeekMonday(today)), dateTo: fmt(today), label: "This Week" };
    case "THIS_MONTH":
      return { dateFrom: fmt(today.startOf("month")), dateTo: fmt(today), label: "This Month" };
    case "LAST_30_DAYS":
      return { dateFrom: fmt(today.subtract(29, "day")), dateTo: fmt(today), label: "Last 30 Days" };
    case "THIS_YEAR":
      return { dateFrom: fmt(today.startOf("year")), dateTo: fmt(today), label: "This Year" };
    case "CUSTOM":
      return { dateFrom: customFrom || fmt(today), dateTo: customTo || fmt(today), label: "Custom" };
    default:
      return { dateFrom: fmt(today.startOf("month")), dateTo: fmt(today), label: "This Month" };
  }
};

// ---- Section 5: context-aware Compare menu ----
// Each option carries its own resolver over the *current* period's
// {dateFrom, dateTo} rather than assuming a fixed period shape - this is
// what lets the same engine serve a single day, a week, a month, or an
// arbitrary custom range without special-casing each one.
const shiftByDays = (n) => (dateFrom, dateTo) => ({
  dateFrom: fmt(dayjs(dateFrom).subtract(n, "day")),
  dateTo: fmt(dayjs(dateTo).subtract(n, "day")),
});
const shiftByMonths = (n) => (dateFrom, dateTo) => ({
  dateFrom: fmt(dayjs(dateFrom).subtract(n, "month")),
  dateTo: fmt(dayjs(dateTo).subtract(n, "month")),
});
const shiftByYears = (n) => (dateFrom, dateTo) => ({
  dateFrom: fmt(dayjs(dateFrom).subtract(n, "year")),
  dateTo: fmt(dayjs(dateTo).subtract(n, "year")),
});
// "Previous period of the same length" - the only shift that needs to know
// the current window's actual span, so it works correctly for This Week/
// Last 30 Days/Custom alike without a dedicated case per period.
const previousPeriodOfSameLength = (dateFrom, dateTo) => {
  const days = dayjs(dateTo).diff(dayjs(dateFrom), "day") + 1;
  return {
    dateFrom: fmt(dayjs(dateFrom).subtract(days, "day")),
    dateTo: fmt(dayjs(dateTo).subtract(days, "day")),
  };
};

const COMPARE_DEFS = {
  YESTERDAY: { label: "Yesterday", resolve: shiftByDays(1) },
  SAME_DAY_LAST_WEEK: { label: "Same Day Last Week", resolve: shiftByDays(7) },
  SAME_DAY_LAST_MONTH: { label: "Same Day Last Month", resolve: shiftByMonths(1) },
  SAME_DAY_LAST_YEAR: { label: "Same Day Last Year", resolve: shiftByYears(1) },
  PREVIOUS_PERIOD: { label: "Previous Period", resolve: previousPeriodOfSameLength },
  PREVIOUS_WEEK: { label: "Previous Week", resolve: shiftByDays(7) },
  SAME_WEEK_LAST_YEAR: { label: "Same Week Last Year", resolve: shiftByYears(1) },
  PREVIOUS_MONTH: { label: "Previous Month", resolve: shiftByMonths(1) },
  SAME_MONTH_LAST_YEAR: { label: "Same Month Last Year", resolve: shiftByYears(1) },
  PREVIOUS_QUARTER: { label: "Previous Quarter", resolve: shiftByMonths(3) },
  PREVIOUS_YEAR: { label: "Previous Year", resolve: shiftByYears(1) },
  SAME_PERIOD_LAST_YEAR: { label: "Same Period Last Year", resolve: shiftByYears(1) },
  // Multi-year entries are handled separately by resolveComparePeriod
  // (they return several windows, not one) - see MULTI_YEAR_KEYS below.
  MULTI_YEAR_3: { label: "Last 3 Years", years: 3 },
  MULTI_YEAR_5: { label: "Last 5 Years", years: 5 },
  MULTI_YEAR_10: { label: "Last 10 Years", years: 10 },
};

const MULTI_YEAR_KEYS = new Set(["MULTI_YEAR_3", "MULTI_YEAR_5", "MULTI_YEAR_10"]);
export const isMultiYearCompare = (compareKey) => MULTI_YEAR_KEYS.has(compareKey);

// Menus are additive per period (a longer/more-analytical period unlocks
// more options), matching your section 5 examples exactly rather than
// showing every key everywhere.
const COMPARE_MENU_BY_PERIOD = {
  TODAY: ["YESTERDAY", "SAME_DAY_LAST_WEEK", "SAME_DAY_LAST_MONTH", "SAME_DAY_LAST_YEAR"],
  YESTERDAY: ["SAME_DAY_LAST_WEEK", "SAME_DAY_LAST_MONTH", "SAME_DAY_LAST_YEAR"],
  THIS_WEEK: ["PREVIOUS_WEEK", "SAME_WEEK_LAST_YEAR"],
  THIS_MONTH: ["PREVIOUS_MONTH", "SAME_MONTH_LAST_YEAR", "PREVIOUS_YEAR", "MULTI_YEAR_3", "MULTI_YEAR_5", "MULTI_YEAR_10"],
  LAST_30_DAYS: ["PREVIOUS_PERIOD", "SAME_PERIOD_LAST_YEAR"],
  THIS_YEAR: ["PREVIOUS_YEAR", "MULTI_YEAR_3", "MULTI_YEAR_5", "MULTI_YEAR_10"],
  CUSTOM: ["PREVIOUS_PERIOD", "SAME_PERIOD_LAST_YEAR"],
};

// "Custom..." is always available, on every period - a manually-picked
// comparison range makes sense regardless of what the current period is.
export const getAvailableCompareOptions = (periodKey) => {
  const keys = COMPARE_MENU_BY_PERIOD[periodKey] || COMPARE_MENU_BY_PERIOD.THIS_MONTH;
  return [...keys.map((key) => ({ value: key, label: COMPARE_DEFS[key].label })), { value: "CUSTOM", label: "Custom..." }];
};

// Returns either a single comparison window ({ type: "single", dateFrom,
// dateTo, label }) or, for a multi-year compare key, several
// ({ type: "multi", windows: [{ dateFrom, dateTo, label }], label }) -
// callers branch on `type` rather than guessing the shape from the key.
// `customFrom`/`customTo` are only read for compareKey === "CUSTOM".
export const resolveComparePeriod = (dateFrom, dateTo, compareKey, customFrom, customTo) => {
  if (compareKey === "CUSTOM") {
    if (!customFrom || !customTo) return null;
    return { type: "single", dateFrom: customFrom, dateTo: customTo, label: "Custom" };
  }

  const def = COMPARE_DEFS[compareKey];
  if (!def) return null;

  if (isMultiYearCompare(compareKey)) {
    const windows = [];
    for (let i = 1; i < def.years; i++) {
      const shifted = shiftByYears(i)(dateFrom, dateTo);
      windows.push({ ...shifted, label: dayjs(shifted.dateFrom).format("YYYY") });
    }
    return { type: "multi", windows, label: def.label };
  }

  const shifted = def.resolve(dateFrom, dateTo);
  return { type: "single", ...shifted, label: def.label };
};
